import fixEncoding from './fixEncoding';

/**
 * Convierte el nombre de una variación ("Azul Rey", "Blanco - Negro", "Silver-Bamboo",
 * "Rojo Traslucido"…) en el fondo CSS de una muestra de color.
 * Devuelve null si el nombre no contiene ningún color reconocible ("Default Title", "16GB").
 */

// Frases de varias palabras primero: se buscan antes que los colores sueltos.
const PHRASES = [
  ['azul rey', '#1E40AF'], ['azul royal', '#1E40AF'], ['royal blue', '#1E40AF'], ['azulrey', '#1E40AF'],
  ['azul marino', '#1E2A5A'], ['navy blue', '#1E2A5A'], ['azul navy', '#1E2A5A'],
  ['azul oscuro', '#1E3A8A'], ['azul claro', '#7DB7F0'], ['azul cielo', '#87CEEB'],
  ['azul pastel', '#A7C7E7'], ['azul petroleo', '#155E75'], ['agua marina', '#2BB5B8'],
  ['verde limon', '#9ACD32'], ['verde lima', '#9ACD32'], ['verde manzana', '#7CC242'],
  ['verde esmeralda', '#0F9D58'], ['verde militar', '#4B5320'], ['verde oscuro', '#166534'],
  ['verde claro', '#86D69B'], ['verde pastel', '#B5E3C1'],
  ['gris oscuro', '#4B5563'], ['gris claro', '#D1D5DB'], ['gris oxford', '#3F4652'],
  ['rojo oscuro', '#8B1A1A'], ['vino tinto', '#6D1A2C'], ['oro rosa', '#E0A899'],
  ['rosa pastel', '#F8C8DC'], ['morado pastel', '#C8A2C8'], ['amarillo pastel', '#FDF0A6'],
  ['amarillo mostaza', '#D4A017'], ['amarillo claro', '#FDE68A'],
  ['cafe claro', '#B08968'], ['marron claro', '#B08968'], ['blanco hueso', '#F2EBDC'],
];

const COLORS = {
  negro: '#1A1A1A', negra: '#1A1A1A',
  blanco: '#FFFFFF', blanca: '#FFFFFF',
  rojo: '#D32F2F', roja: '#D32F2F', tinto: '#7B1E2B', vinotinto: '#6D1A2C',
  azul: '#2563EB', celeste: '#7EC8E3', navy: '#1E2A5A', aqua: '#2BC4C4',
  verde: '#2E9E4F', lima: '#9ACD32', menta: '#98E2C6', pino: '#2F5D3A',
  amarillo: '#FACC15', amarilla: '#FACC15', mostaza: '#D4A017',
  naranja: '#F57C00',
  gris: '#9CA3AF', humo: '#6B7280',
  morado: '#7E3FA0', morada: '#7E3FA0', lila: '#C8A2C8', violeta: '#8B5CF6', purpura: '#7E3FA0',
  rosa: '#F48FB1', rosado: '#F48FB1', rosada: '#F48FB1', fucsia: '#D81B87', salmon: '#FA8072',
  silver: '#C0C4CC', plata: '#C0C4CC', plateado: '#C0C4CC',
  dorado: '#D4AF37', oro: '#D4AF37', cobre: '#B87333',
  beige: '#E8DCC4', hueso: '#F2EBDC', marfil: '#FFFFF0', khaki: '#C3B091', camel: '#C19A6B',
  cafe: '#6F4E37', marron: '#7B4B2A',
  natural: '#E6D3B3', trigo: '#E3C99A', craft: '#C8A27A', yute: '#C2A878', carton: '#B88B5A',
  bambu: '#D9B77E', bamboo: '#D9B77E', madera: '#A0723C', corcho: '#C69C6D',
  transparente: 'transparent',
};

// Varias tonalidades en una sola muestra.
const MULTI_WORDS = ['multicolor', 'surtido', 'surtidos', 'tricolor', 'varios', 'colores'];
const RAINBOW = 'conic-gradient(#EF4444, #F59E0B, #FACC15, #22C55E, #3B82F6, #8B5CF6, #EF4444)';

// Tablero de ajedrez que se ve detrás de un color transparente o traslúcido.
const CHECKER = 'repeating-conic-gradient(#E5E7EB 0% 25%, #FFFFFF 0% 50%) 50% / 10px 10px';

const normalize = (name) => fixEncoding(String(name || ''))
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** Colores en el orden en que aparecen en el nombre (sin repetir), máximo 3. */
const findColors = (text) => {
  const found = [];
  const padded = ` ${text} `;
  const taken = [];
  const add = (index, hex, length) => {
    if (taken.some(([start, end]) => index < end && index + length > start)) return;
    taken.push([index, index + length]);
    found.push([index, hex]);
  };

  PHRASES.forEach(([phrase, hex]) => {
    let i = padded.indexOf(` ${phrase} `);
    while (i !== -1) {
      add(i, hex, phrase.length + 1);
      i = padded.indexOf(` ${phrase} `, i + 1);
    }
  });
  text.split(' ').reduce((offset, word) => {
    if (COLORS[word]) add(offset, COLORS[word], word.length + 1);
    return offset + word.length + 1;
  }, 0);

  return [...new Set(found.sort((a, b) => a[0] - b[0]).map(([, hex]) => hex))].slice(0, 3);
};

/** Diagonal con franjas duras: dos o tres colores en el mismo círculo. */
const splitGradient = (hexes) => {
  const step = Math.round(100 / hexes.length);
  const stops = hexes.map((hex, i) => `${hex} ${i * step}% ${i === hexes.length - 1 ? 100 : (i + 1) * step}%`);
  return `linear-gradient(135deg, ${stops.join(', ')})`;
};

/**
 * @returns {{ background: string, light: boolean } | null}
 *   `light` indica un color muy claro (blanco, transparente…) que necesita borde más marcado.
 */
const colorSwatch = (name) => {
  const text = normalize(name);
  if (!text) return null;

  if (MULTI_WORDS.some((w) => text.split(' ').includes(w))) {
    return { background: RAINBOW, light: false };
  }

  const isClear = /\b(traslucido|translucido|transparente|transp|cristal|frost|frosty)\b/.test(text);
  const isMetallic = /\b(metalizado|metalico|holografico)\b/.test(text);
  let hexes = findColors(text);
  if (hexes.length === 0) return null;
  if (hexes.length === 1 && hexes[0] === 'transparent') return { background: CHECKER, light: true };
  // "Azul transparente", "Transparente/Negro/Rojo": la transparencia es un acabado, no una franja.
  hexes = hexes.filter((hex) => hex !== 'transparent');

  // En el shorthand `background` solo la última capa puede ser un color plano: todo va como imagen.
  const solid = (hex) => `linear-gradient(${hex}, ${hex})`;
  const base = hexes.length === 1 ? solid(isClear ? `${hexes[0]}8C` : hexes[0]) : splitGradient(hexes);
  const layers = [base];
  if (isMetallic) {
    layers.unshift('linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 45%, rgba(0,0,0,0.18) 100%)');
  }
  if (isClear) layers.push(CHECKER);

  const light = isClear || hexes.some((hex) => ['#FFFFFF', '#FFFFF0', '#F2EBDC'].includes(hex));
  return { background: layers.join(', '), light };
};

export default colorSwatch;
